import { closeCompletion } from '@codemirror/autocomplete';
import {
  foldedRanges,
  forceParsing,
  indentUnit,
  syntaxTreeAvailable,
} from '@codemirror/language';
import {
  Compartment,
  EditorSelection,
  EditorState,
  Extension,
  Prec,
  Transaction,
} from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import clsx from 'clsx';
import {
  forwardRef,
  type MutableRefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type AriaLabelingProps,
} from '@dynatrace/strato-components/core';

import { editorContainerCSS } from './BaseCodeEditor.sty.js';
import type { EditorLanguageConfiguration } from './configuration/configuration.js';
import { configureLineWrap } from './configuration/configure-line-wrap.js';
import { configurePlaceholder } from './configuration/configure-placeholder.js';
import { configureReadOnly } from './configuration/configure-read-only.js';
import { configureSpellCheck } from './configuration/configure-spell-check.js';
import {
  getMultipleLinesDefaultConfiguration,
  getSingleLineDefaultConfiguration,
  getDefaultConfiguration,
} from './configuration/getDefaultConfigurations.js';
import type { GutterConfiguration } from './configuration/getDefaultConfigurations.js';
import { getLanguageConfiguration } from './configuration/language-extensions.js';
import { highlightExtension } from './highlight/highlight.extension.js';
import {
  dataTestId as dataTestIdAttr,
  baseCodeEditorTestId,
  baseCodeEditorRootTestId,
  baseCodeEditorScrollerTestId,
} from './testIds.js';
import { calculateFolding } from './utils/calculate-folding.js';
import { getKeyMaps } from './utils/key-maps.js';
import { IntlProps } from './utils/render-node.js';
import { runFoldings } from './utils/run-foldings.js';
import { sanitizeText } from './utils/sanitize-text.js';
import { sanitizeValue } from './utils/sanitize-value.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import type { FormControlStateProps } from '../../core/types/form-control-state.js';
import { InternalBaseItem as BaseItem } from '../../forms/forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useSyncedFormControlProps } from '../../forms/forms-core/hooks/useSyncedFormControlProps.js';
import { inputGroupClassName } from '../../layouts/input-group/InputGroup.sty.js';

// We disable the unstable EDIT_CONTEXT feature to ensure codemirror doesn't capture input after losing focus.
// According to the maintainer it should be fixed with an upcoming chrome release, but we don't know about about Safari.
// Github Issue https://github.com/codemirror/dev/issues/1537
// @ts-expect-error - the prop exists but seems not to be included in the codemirror types.
EditorView.EDIT_CONTEXT = false;

// #region typings
/**
 * Props used for BaseCodeEditor
 * @internal
 **/
export interface BaseCodeEditorProps
  extends FormControlStateProps,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps {
  /** The id for the DOM element */
  id?: string;
  /** The content of the editor
   * @defaultValue ''
   */
  value?: string;
  /** Callback that is called when the value in the editor changes. */
  onChange?: (value: string) => void;
  /** Displayed initially in the editor when there is no other content. */
  placeholder?: string;
  /** Whether spellcheck should be enabled. */
  spellCheck?: boolean;
  /**
   * If set to true, the code editor uses the full height available in its parent.
   * @defaultValue false
   */
  fullHeight?: boolean;
  /** The start indices (character position) that should be folded initially in uncontrolled scenarios. */
  defaultFolding?: number[];
  /** The start indices (character position) that should be folded initially in controlled scenarios.*/
  folding?: number[];
  /** Callback that is called when folding changes. */
  onFoldingChange?: (values: number[]) => void;
  /** Whether the input is readonly
   * @defaultValue false
   */
  readOnly?: boolean;
  /** Whether long lines should be wrapped
   * @defaultValue false
   */
  lineWrap?: boolean;
  /** The language config */
  configuration?: EditorLanguageConfiguration;
  /**
   * If set to true, the code editor allows only one line and the line number will not be displayed.
   * @defaultValue false
   */
  singleLine?: boolean;

  /**
   * If set to true, the code editor will hide the line numbers
   * @defaultValue false
   */
  hideLineNumbers?: boolean;

  /** Callback that is called when the editor loses focus. */
  onBlur?: (e: FocusEvent) => void;
  /** Callback that is called when the editor receives focus. */
  onFocus?: (e: FocusEvent) => void;

  /**
   * Provides the gutter configuration to display a gutter next to the fold gutter
   */
  gutterConfiguration?: GutterConfiguration;

  /**
   * Editor layout size, 'default' for standard spacing and 'condensed' for reduced font size, padding and margins.
   * @defaultValue 'default'
   */
  size?: 'default' | 'condensed';
  /**
   * If set to true, `aria-required` will be set to true on the input element.
   * @defaultValue false
   */
  required?: boolean;
}

/**
 * public ref type
 * @internal
 */
export type BaseCodeEditorRef = {
  focus: () => void;
  selectRange: (range: { from: number; to: number }) => void;
  readonly element: HTMLDivElement | null;
  setCursorPosition: (position: number | 'end') => void;
  editorView?: () => EditorView;
};
// #endregion typings

const languageCompartment = new Compartment();
const lineWrappingCompartment = new Compartment();
const keymapCompartment = new Compartment();
const placeholderCompartment = new Compartment();
const highlightOnChangeCompartment = new Compartment();

const getEditorConfiguration = (
  intlProps: IntlProps,
  options: {
    singleLine: boolean;
    hideLineNumbers?: boolean;
    gutterConfiguration?: GutterConfiguration;
    required?: boolean;
    size?: 'default' | 'condensed';
  },
): Extension[] => {
  const { singleLine, gutterConfiguration, hideLineNumbers, required, size } =
    options;

  const isRequired = required ?? false;
  const editorSize = size ?? 'default';

  if (singleLine) {
    return getSingleLineDefaultConfiguration(intlProps, {
      required: isRequired,
      size: editorSize,
    });
  }
  if (hideLineNumbers) {
    return getDefaultConfiguration(intlProps, {
      required: isRequired,
      size: editorSize,
    });
  }
  return getMultipleLinesDefaultConfiguration(intlProps, {
    gutterConfiguration,
    required: isRequired,
    size: editorSize,
  });
};

function useLazyEditorViewRef(): MutableRefObject<EditorView> {
  const viewRef = useRef<EditorView | null>(null);

  if (viewRef.current === null) {
    viewRef.current = new EditorView({
      state: EditorState.create(),
    });
  }

  // it can never be null, the cast fixes the type
  return viewRef as MutableRefObject<EditorView>;
}

/**
 * Provides a base/core component for other code editors to share a common base.
 *
 * @internal
 */
export const BaseCodeEditor = /* @__PURE__ */ forwardRef<
  BaseCodeEditorRef,
  BaseCodeEditorProps
>((props, forwardedRef) => {
  const {
    controlState,
    readOnly = false,
    spellCheck = false,
    lineWrap = false,
    onChange,
    onFoldingChange,
    defaultFolding,
    folding,
    fullHeight = false,
    placeholder,
    configuration,
    singleLine = false,
    hideLineNumbers = false,
    size = 'default',
    onFocus,
    onBlur,
    gutterConfiguration,
    style: consumerStyle,
    className: consumerClassName,
    'data-testid': dataTestId = baseCodeEditorRootTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-details': ariaDetails,
    'aria-describedby': ariaDescribedBy,
    id: propId,
    required = false,
  } = props;

  const intl = useIntl();
  const [hasError, hint] = useFormControlState(controlState);
  const containerRef = useRef<HTMLDivElement>(null);

  const rootRef = useMergeRefs<HTMLDivElement>([containerRef]);

  /**
   * Sanitize value
   */
  const sanitizedOnChange = useCallback(
    (value: string) => onChange?.(sanitizeText(value)),
    [onChange],
  );

  const viewRef = useLazyEditorViewRef();

  const { id } = useSyncedFormControlProps({
    propId,
  });

  const valueChangeRef = useRef<(value: string) => void>(undefined);
  // Reference of the currently applied value, stored in a ref to be accessed correctly inside the codeMirror listeners.
  const valueRef = useRef('');
  // Flag indicating that the document has been changed by someone other than the user. Mainly through setting the value prop.
  const isMarkedDirtyRef = useRef<boolean>(false);

  const [previousValue, setPreviousValue] = useState<string>('');
  // Reference of the transaction where the doc (CodeMirror value) was last changed. Used mainly to get most up-to-date cursor position.
  const transactionRef = useRef<Transaction | undefined>(undefined);

  const { focusProps, isFocusVisible } = useFocusRing({
    color: hasError ? 'critical' : 'neutral',
  });

  const [controlledFolding] = useControllableState<number[] | undefined>({
    defaultValue: defaultFolding,
    onChange: (values?: number[]) => {
      if (values) {
        onFoldingChange?.(values);
      }
    },
    value: folding,
  });

  useImperativeHandle(
    forwardedRef,
    () => ({
      element: containerRef.current,
      editorView: () => viewRef.current,
      focus: () => viewRef.current.focus(),
      selectRange: (range) => {
        const editorView = viewRef.current;
        if (editorView && range) {
          const docLength = editorView.state.doc.length;
          const from = range.from < docLength ? range.from : 0;
          const to = range.to < docLength ? range.to : docLength;

          const transaction = editorView.state.update({
            selection: EditorSelection.range(to, from),
            scrollIntoView: true,
          });
          editorView.dispatch(transaction);
          editorView.focus();
        }
      },
      setCursorPosition: (position: number | 'end') => {
        viewRef.current.dispatch({
          selection: EditorSelection.cursor(
            typeof position === 'number'
              ? Math.min(
                  Math.max(position, 0),
                  viewRef.current.state.doc.length,
                )
              : viewRef.current.state.doc.length,
          ),
          scrollIntoView: true,
        });
        viewRef.current.focus();
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Create editor view
  useEffect(() => {
    const editorView = viewRef.current;
    containerRef.current?.appendChild(editorView.dom);
    editorView.dom.setAttribute(dataTestIdAttr, baseCodeEditorTestId);

    return () => editorView.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sets aria labels
  useEffect(() => {
    const { contentDOM } = viewRef.current;
    const ariaLabelAttribute = 'aria-label';
    const ariaLabelledByAttribute = 'aria-labelledby';
    const ariaDetailsAttribute = 'aria-details';
    const ariaDescribedByAttribute = 'aria-describedby';

    if (ariaDescribedBy) {
      contentDOM.setAttribute(ariaDescribedByAttribute, ariaDescribedBy);
    }

    if (ariaDetails) {
      contentDOM.setAttribute(ariaDetailsAttribute, ariaDetails);
    }

    if (ariaLabelledBy) {
      contentDOM.setAttribute(ariaLabelledByAttribute, ariaLabelledBy);
    } else {
      contentDOM.setAttribute(
        ariaLabelAttribute,
        ariaLabel ??
          intl.formatMessage({
            defaultMessage: 'Code Editor',
            id: 'EBcYgbL0S1T6EpS2',
            description: 'Aria-label for the code editors.',
          }),
      );
    }

    return () =>
      [
        ariaLabelAttribute,
        ariaLabelledByAttribute,
        ariaDetailsAttribute,
        ariaDescribedByAttribute,
      ].forEach((ariaAttribute) => contentDOM.removeAttribute(ariaAttribute));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ariaLabel, ariaLabelledBy, ariaDetails, ariaDescribedBy, intl]);

  // to match the css applied to the .cm-content based on size we need to remeasure the height
  // useLayoutEffect to match with the css change
  useLayoutEffect(() => {
    viewRef.current.requestMeasure();
  }, [viewRef, size]);

  const { messages, defaultLocale, locale } = useIntl();

  // Set state and extensions of the editor
  useEffect(() => {
    const editorView = viewRef.current;

    const handleValueChange = (transaction: Transaction) => {
      const newValue = transaction.newDoc.toString();

      if (valueRef.current !== newValue) {
        // Save the current transaction that updates the document to grab the most up-to-date selection range (cursor position).
        // This was done to fix cursor position resetting in APPDEV-9116.
        transactionRef.current = transaction;

        // Update the value ref
        valueRef.current = newValue;

        // Avoid calling the rest of logic including onChange if changes are coming from the value prop
        if (isMarkedDirtyRef.current) {
          isMarkedDirtyRef.current = false;
        } else {
          // Call the onChange from the editor props
          valueChangeRef.current?.(newValue);
        }
      } else {
        // Reset the marked dirty flag
        isMarkedDirtyRef.current = false;
      }
      return transaction;
    };

    editorView.setState(
      EditorState.create({
        // Initial value of the editor
        doc: '',
        extensions: [
          Prec.highest(keymapCompartment.of(getKeyMaps(viewRef))),
          languageCompartment.of([]),
          // Indent unit is two spaces instead of tab
          indentUnit.of('  '),
          // Filter for transactions that gets applied before they are processed. This allows us
          // to control which transactions go through as well as adding/editing transactions if we please.
          EditorState.transactionFilter.of((transaction) => {
            //TODO: Update this logic for controlled/uncontrolled implementation in APPDEV-5775
            if (transaction.docChanged) {
              return handleValueChange(transaction);
            }
            return transaction;
          }),
          EditorView.updateListener.of((update) => {
            const isUndoOrRedo = update.transactions.some(
              (transaction) =>
                transaction.isUserEvent('undo') ||
                transaction.isUserEvent('redo'),
            );

            // Make sure that only folding was changed in the editor
            if (
              update.geometryChanged &&
              !update.docChanged &&
              !update.focusChanged &&
              update.heightChanged
            ) {
              const ranges = foldedRanges(update.state);
              const foldValues = calculateFolding(ranges);

              onFoldingChange?.(foldValues);
            }
            // Check if the transaction is undo or redo in order to not accidentally call onChange
            // twice. Usually onChange is called before applying the changes. However, undo and redo
            // are considered as transactions that occur after the view has been updated.
            // Therefore, we explicitly call onChange if that is the case.
            else if (update.docChanged && isUndoOrRedo) {
              update.transactions.forEach((transaction) =>
                handleValueChange(transaction),
              );
            }
          }),
          lineWrappingCompartment.of([]),
          placeholderCompartment.of([]),
          highlightOnChangeCompartment.of([]),
        ],
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update language configuration
  useEffect(() => {
    const editorView = viewRef.current;
    const intlProps: IntlProps = {
      locale,
      defaultLocale,
      messages,
    };
    const extensions = [
      ...getEditorConfiguration(intlProps, {
        singleLine,
        hideLineNumbers,
        gutterConfiguration,
        required,
      }),
      ...getLanguageConfiguration(configuration || {}, {
        messages,
        defaultLocale,
        locale,
      }),
    ];
    editorView.dispatch({
      effects: languageCompartment.reconfigure(extensions),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    configuration,
    defaultLocale,
    locale,
    messages,
    singleLine,
    hideLineNumbers,
    gutterConfiguration,
  ]);

  useEffect(() => {
    configureReadOnly(readOnly, viewRef, keymapCompartment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  useEffect(() => {
    configureLineWrap(lineWrap, viewRef, lineWrappingCompartment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineWrap]);

  useEffect(() => {
    configurePlaceholder(placeholder, viewRef, placeholderCompartment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholder]);

  useEffect(() => {
    configureSpellCheck(spellCheck, readOnly, viewRef);
  }, [viewRef, readOnly, spellCheck]);

  // Assign new onChange method to ref that is used in the updatelistener extension
  useEffect(() => {
    valueChangeRef.current =
      onChange !== undefined ? sanitizedOnChange : undefined;
  }, [onChange, sanitizedOnChange]);

  // Set the content of the code editor to the changed value
  useEffect(() => {
    // TODO: Update this logic for controlled/uncontrolled implementation in APPDEV-5775
    const sanitizedValue = sanitizeValue(props.value);
    if (viewRef.current.state.doc.toString() === sanitizedValue) {
      return;
    }

    // Keep track of the previous value, mainly for highlighting purposes
    setPreviousValue(viewRef.current.state.doc.toString());

    // Set flag to true to indicate it was changed by someone other than user
    isMarkedDirtyRef.current = true;

    try {
      let selection;
      if (!viewRef.current.hasFocus) {
        // If the editor doesn't have focus, replace entire doc
        // with new value regardless of cursor position
        selection = undefined;
      } else {
        // Fallback to the current view cursor position
        // if the transaction is undefined
        selection =
          transactionRef.current !== undefined
            ? transactionRef.current.selection
            : viewRef.current.state.selection.main;
      }
      // Reset the transaction reference
      transactionRef.current = undefined;

      // Create a transaction to update the codeMirror document with the new value
      const transaction: Transaction = viewRef.current.state.update({
        changes: [
          {
            from: 0,
            to: viewRef.current.state.doc.length,
            insert: sanitizedValue,
          },
        ],
        annotations: [Transaction.addToHistory.of(false)],
        selection,
      });

      viewRef.current.dispatch(transaction);
    } catch {
      /*
    Left empty on purpose. This was done to catch RangeErrors which would be thrown
    if the user is typing very fast. The selection cursor would be outside the CodeMirror's document length in some cases.
    Catching the error is better than crashing the entire code editor. */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  // Run the highlight extension while providing the previous value
  useEffect(() => {
    const editorView = viewRef.current;
    editorView.dispatch({
      effects: highlightOnChangeCompartment.reconfigure([
        highlightExtension(previousValue),
      ]),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousValue]);

  useEffect(() => {
    runFoldings(controlledFolding ?? [], viewRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledFolding]);

  // add aria-invalid if form validation returns an error
  useEffect(() => {
    const codeEditorInput = viewRef.current.dom.querySelector(
      'div[contenteditable]',
    );
    if (codeEditorInput) {
      hasError
        ? codeEditorInput.setAttribute('aria-invalid', 'true')
        : codeEditorInput.removeAttribute('aria-invalid');
      (codeEditorInput as HTMLElement).tabIndex = -1;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  const focusListener = useCallback(
    (e: FocusEvent) => {
      onFocus?.(e);
    },
    [onFocus],
  );

  const blurListener = useCallback(
    (e: FocusEvent) => {
      onBlur?.(e);
    },
    [onBlur],
  );

  useEffect(() => {
    const codeEditorInput = viewRef.current.dom.querySelector(
      'div[contenteditable]',
    ) as HTMLElement;
    codeEditorInput?.addEventListener('focus', focusListener);
    codeEditorInput?.addEventListener('blur', blurListener);
    return () => {
      codeEditorInput?.removeEventListener('focus', focusListener);
      codeEditorInput?.removeEventListener('blur', blurListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blurListener, focusListener]);

  // Handle clicking outside the editor while having the autocomplete open to close it.
  const handleOutsideEditorClick = useCallback(
    (e: MouseEvent) => {
      const autoCompleteContainer = document.body.querySelector(
        '.cm-tooltip-autocomplete',
      );
      if (!autoCompleteContainer?.contains(e.target as Node)) {
        closeCompletion(viewRef.current);
      }
    },
    [viewRef],
  );

  useEffect(() => {
    // This way is preferred over handling it through the blur event on the editor as that closes the autocomplete
    // if the user attempts to scroll in the autocomplete tooltip
    // https://dev-jira.dynatrace.org/browse/ADES-4842
    document.addEventListener('pointerdown', handleOutsideEditorClick);
    return () => {
      document.removeEventListener('pointerdown', handleOutsideEditorClick);
    };
  }, [handleOutsideEditorClick]);

  // https://dev-jira.dynatrace.org/browse/EXA-5783
  // forceParsing is a synchronous operation that blocks the main thread, but it keeps track
  // of the already parsed content. We parse in chunks, each blocking the main thread for 100ms
  // but pausing for 100ms after each parse attempt, this keeps the page responsive to scrolling and clicks
  // Note: This is really only a issue for very large json content like: `fetch spans | limit 5000` ( ~ 13 MB )
  useEffect(() => {
    if (!props.value) {
      return;
    }

    let timeoutId: number | null = null;
    let done = false;

    const cleanup = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };

    const tick = () => {
      const wholeSyntaxTreeParsed = syntaxTreeAvailable(
        viewRef.current.state,
        viewRef.current.state.doc.length,
      );

      if (wholeSyntaxTreeParsed) {
        cleanup();
        return;
      }

      done = forceParsing(
        viewRef.current,
        viewRef.current.state.doc.length,
        100,
      );

      if (!done) {
        timeoutId = window.setTimeout(tick, 10);
      }
    };

    tick();

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  // change the editor's focus behavior
  // only the .cm-scroll container is focusable via the keyboard, the content can be focused
  // by pressing 'ENTER' and only by pressing 'ESCAPE' the focus is moved back
  useEffect(() => {
    const editorScrollContainer =
      viewRef.current.dom.querySelector('.cm-scroller');
    editorScrollContainer?.setAttribute('tabIndex', '0');
    editorScrollContainer?.setAttribute(
      dataTestIdAttr,
      baseCodeEditorScrollerTestId,
    );

    const handleFocus = (event: Event) => {
      if (event instanceof FocusEvent) {
        focusProps.onFocus(event);
      }
    };
    const handleBlur = (event: Event) => {
      if (event instanceof FocusEvent) {
        focusProps.onBlur(event);
      }
    };
    const handleKeyDown = (event: Event) => {
      if (event instanceof KeyboardEvent) {
        if (
          event.key === 'Enter' &&
          !event.metaKey &&
          !event.ctrlKey &&
          !readOnly
        ) {
          event.preventDefault();
          viewRef.current.focus();
        }
      }
    };
    editorScrollContainer?.addEventListener('focus', handleFocus);
    editorScrollContainer?.addEventListener('blur', handleBlur);
    editorScrollContainer?.addEventListener('keydown', handleKeyDown);

    return () => {
      editorScrollContainer?.removeEventListener('focus', handleFocus);
      editorScrollContainer?.removeEventListener('blur', handleBlur);
      editorScrollContainer?.removeEventListener('keydown', handleKeyDown);
    };
  }, [focusProps, readOnly, viewRef]);

  return (
    <>
      <div
        ref={rootRef}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        className={clsx(
          consumerClassName,
          editorContainerCSS({
            focusVisible: isFocusVisible,
            fullHeight,
            hasError,
            lineNumbers: singleLine || hideLineNumbers ? 'hidden' : undefined,
            readOnly,
            size,
          }),
          inputGroupClassName,
        )}
        style={consumerStyle}
      />
      <input
        type="text"
        id={id}
        disabled={readOnly}
        tabIndex={-1}
        onFocus={() =>
          // forward focus to the editor
          // we need the hidden input unfortunately since the editor does not support focus properly
          viewRef.current.focus()
        }
        aria-hidden="true"
        style={{
          position: 'absolute',
          height: 0,
          width: 0,
          opacity: 0,
          margin: 0,
          padding: 0,
          border: 0,
        }}
      />
      {(hasError || hint) && (
        <span
          aria-live="polite"
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
        >
          <BaseItem variant={hasError ? 'error' : 'hint'}>{hint}</BaseItem>
        </span>
      )}
    </>
  );
});

(
  BaseCodeEditor as typeof BaseCodeEditor & { displayName: string }
).displayName = 'BaseCodeEditor';
